import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  BarChart3, 
  Users, 
  Eye, 
  Globe, 
  Monitor, 
  Smartphone,
  TrendingUp,
  Clock,
  MapPin,
  ExternalLink,
  Download,
  Radio
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar chart component - defined outside main component to avoid re-renders
function SimpleBarChart({ data, maxValue }) {
  if (!data || data.length === 0) return <p className="text-zinc-400 text-sm">No data</p>;
  const max = maxValue || Math.max(...data.map(d => d.value));
  
  return (
    <div className="space-y-2">
      {data.slice(0, 7).map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="w-20 text-xs text-zinc-500 truncate">{item.label}</div>
          <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden">
            <div 
              className="h-full bg-[#FF66C4] transition-all duration-500"
              style={{ width: `${(item.value / max) * 100}%` }}
            />
          </div>
          <div className="w-12 text-xs text-zinc-600 text-right">{item.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsDashboard({ token }) {
  const [analytics, setAnalytics] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  const [liveVisitorCount, setLiveVisitorCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/overview?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [period, token]);

  const fetchRecentVisitors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/recent-visitors?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentVisitors(response.data);
    } catch (error) {
      console.error("Error fetching recent visitors:", error);
    }
  }, [token]);

  const fetchLiveCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/live-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveVisitorCount(response.data.live_count);
    } catch (error) {
      console.error("Error fetching live count:", error);
    }
  }, [token]);

  const fetchLoginLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/login-logs?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoginLogs(response.data);
    } catch (error) {
      console.error("Error fetching login logs:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchAnalytics();
    fetchRecentVisitors();
    fetchLiveCount();
    fetchLoginLogs();
    
    // Refresh live count every 30 seconds
    const interval = setInterval(fetchLiveCount, 30000);
    return () => clearInterval(interval);
  }, [fetchAnalytics, fetchRecentVisitors, fetchLiveCount, fetchLoginLogs]);

  // Export to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/analytics/recent-visitors?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const visitors = response.data;
      if (visitors.length === 0) {
        alert('No data to export');
        return;
      }
      
      // Create CSV content
      const headers = ['Date', 'Time', 'Page', 'Country', 'City', 'Device', 'Browser', 'OS', 'Referrer', 'IP'];
      const rows = visitors.map(v => {
        const date = new Date(v.visited_at);
        return [
          date.toLocaleDateString(),
          date.toLocaleTimeString(),
          v.page_url,
          v.country || '',
          v.city || '',
          v.device || '',
          v.browser || '',
          v.os || '',
          v.referrer || '',
          v.ip || ''
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      });
      
      const csv = [headers.join(','), ...rows].join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `filmcable-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#FF66C4] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-bold">Analytics Dashboard</h2>
          <p className="text-zinc-500">Track your website performance and visitor insights</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Live Visitors */}
          <div
            className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-none"
            data-testid="live-visitor-count"
          >
            <Radio size={14} className="text-green-500 animate-pulse" />
            <span className="text-sm font-medium text-green-700">
              {liveVisitorCount} live now
            </span>
          </div>
          
          {/* Export Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting}
            className="rounded-none"
            data-testid="analytics-export-btn"
          >
            <Download size={16} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          
          {/* Period Select */}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 rounded-none" data-testid="analytics-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-4 border-b border-zinc-200">
        <button
          onClick={() => setActiveSection("overview")}
          className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
            activeSection === "overview"
              ? "border-[#FF66C4] text-[#FF66C4]"
              : "border-transparent text-zinc-500 hover:text-zinc-900"
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveSection("login-history")}
          data-testid="login-history-tab"
          className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
            activeSection === "login-history"
              ? "border-[#FF66C4] text-[#FF66C4]"
              : "border-transparent text-zinc-500 hover:text-zinc-900"
          }`}
        >
          Login History ({loginLogs.length})
        </button>
      </div>

      {activeSection === "overview" && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-zinc-200 p-6" data-testid="page-views-stat">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#FF66C4]/10 text-[#FF66C4]">
                  <Eye size={20} />
                </div>
                <span className="text-sm font-medium text-zinc-500 uppercase tracking-wider">Page Views</span>
              </div>
              <p className="text-3xl font-bold text-zinc-900">{analytics?.total_views?.toLocaleString() || 0}</p>
            </div>
            
            <div className="bg-white border border-zinc-200 p-6" data-testid="unique-visitors-stat">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#FF66C4]/10 text-[#FF66C4]">
                  <Users size={20} />
                </div>
                <span className="text-sm font-medium text-zinc-500 uppercase tracking-wider">Unique Visitors</span>
              </div>
              <p className="text-3xl font-bold text-zinc-900">{analytics?.unique_visitors?.toLocaleString() || 0}</p>
            </div>
            
            <div className="bg-white border border-zinc-200 p-6" data-testid="unique-ips-stat">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-[#FF66C4]/10 text-[#FF66C4]">
                  <Globe size={20} />
                </div>
            <span className="text-sm font-medium text-zinc-500 uppercase tracking-wider">Unique IPs</span>
          </div>
          <p className="text-3xl font-bold text-zinc-900">{analytics?.unique_ips?.toLocaleString() || 0}</p>
        </div>
        
        <div className="bg-white border border-zinc-200 p-6" data-testid="average-views-stat">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-[#FF66C4]/10 text-[#FF66C4]">
              <TrendingUp size={20} />
            </div>
            <span className="text-sm font-medium text-zinc-500 uppercase tracking-wider">Avg Views/Day</span>
          </div>
          <p className="text-3xl font-bold text-zinc-900">
            {analytics?.daily_views?.length > 0 
              ? Math.round(analytics.total_views / analytics.daily_views.length).toLocaleString()
              : 0}
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Views Chart */}
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
            <BarChart3 size={18} className="text-[#FF66C4]" />
            Daily Views
          </h3>
          <SimpleBarChart 
            data={analytics?.daily_views?.map(d => ({ label: d.date.slice(5), value: d.views })) || []}
          />
        </div>

        {/* Top Countries */}
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
            <MapPin size={18} className="text-[#FF66C4]" />
            Top Countries
          </h3>
          <SimpleBarChart 
            data={analytics?.top_countries?.map(c => ({ label: c.country, value: c.visits })) || []}
          />
        </div>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Browsers */}
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
            <Monitor size={18} className="text-[#FF66C4]" />
            Browsers
          </h3>
          <SimpleBarChart 
            data={analytics?.browsers?.map(b => ({ label: b.browser, value: b.count })) || []}
          />
        </div>

        {/* Devices */}
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
            <Smartphone size={18} className="text-[#FF66C4]" />
            Devices
          </h3>
          <SimpleBarChart 
            data={analytics?.devices?.map(d => ({ label: d.device, value: d.count })) || []}
          />
        </div>

        {/* Top Referrers */}
        <div className="bg-white border border-zinc-200 p-6">
          <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
            <ExternalLink size={18} className="text-[#FF66C4]" />
            Top Referrers
          </h3>
          {analytics?.referrers?.length > 0 ? (
            <div className="space-y-2">
              {analytics.referrers.slice(0, 5).map((ref, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600 truncate max-w-[180px]" title={ref.referrer}>
                    {ref.referrer.replace(/^https?:\/\//, '').split('/')[0]}
                  </span>
                  <span className="text-zinc-900 font-medium">{ref.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-400 text-sm">No referrer data</p>
          )}
        </div>
      </div>

      {/* Top Articles */}
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="font-serif text-lg font-bold mb-4">Top Articles</h3>
        {analytics?.article_views?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Article</th>
                  <th className="text-right py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Views</th>
                </tr>
              </thead>
              <tbody>
                {analytics.article_views.map((article, index) => (
                  <tr key={index} className="border-b border-zinc-100">
                    <td className="py-3">
                      <a 
                        href={article.article} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-zinc-700 hover:text-[#FF66C4] transition-colors"
                      >
                        {article.article.replace('/articles/', '')}
                      </a>
                    </td>
                    <td className="py-3 text-right font-medium">{article.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-zinc-400 text-sm">No article views yet</p>
        )}
      </div>

      {/* Recent Visitors */}
      <div className="bg-white border border-zinc-200 p-6" data-testid="recent-visitors-panel">
        <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
          <Clock size={18} className="text-[#FF66C4]" />
          Recent Visitors
        </h3>
        {recentVisitors.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Time</th>
                  <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Page</th>
                  <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Location</th>
                  <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Device</th>
                  <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Browser</th>
                </tr>
              </thead>
              <tbody>
                {recentVisitors.map((visitor, index) => (
                  <tr key={index} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2 text-zinc-500 whitespace-nowrap">{formatTime(visitor.visited_at)}</td>
                    <td className="py-2 max-w-[200px] truncate" title={visitor.page_url}>{visitor.page_url}</td>
                    <td className="py-2 text-zinc-600">
                      {visitor.city !== 'Unknown' && visitor.city !== 'Local' 
                        ? `${visitor.city}, ${visitor.country}` 
                        : visitor.country}
                    </td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        visitor.device === 'Mobile' ? 'bg-blue-100 text-blue-700' :
                        visitor.device === 'Tablet' ? 'bg-purple-100 text-purple-700' :
                        'bg-zinc-100 text-zinc-700'
                      }`}>
                        {visitor.device}
                      </span>
                    </td>
                    <td className="py-2 text-zinc-600">{visitor.browser}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-zinc-400 text-sm">No visitor data yet. Visitors will appear here once tracking is enabled.</p>
        )}
      </div>
        </>
      )}

      {/* Login History Section */}
      {activeSection === "login-history" && (
        <div className="bg-white border border-zinc-200 p-6" data-testid="login-history-panel">
          <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
            <Users size={18} className="text-[#FF66C4]" />
            Login History
          </h3>
          {loginLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Time</th>
                    <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">User</th>
                    <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Role</th>
                    <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">IP Address</th>
                    <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Device</th>
                    <th className="text-left py-2 font-medium text-zinc-500 uppercase tracking-wider text-xs">Browser</th>
                  </tr>
                </thead>
                <tbody>
                  {loginLogs.map((log, index) => (
                    <tr key={index} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-2 text-zinc-500 whitespace-nowrap">{formatTime(log.logged_in_at)}</td>
                      <td className="py-2">
                        <div>
                          <span className="font-medium">{log.writer_name || log.username}</span>
                          {log.writer_name && (
                            <span className="text-zinc-400 text-xs ml-1">@{log.username}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          log.role === 'admin' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {log.role}
                        </span>
                      </td>
                      <td className="py-2 text-zinc-600 font-mono text-xs">{log.ip}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          log.device === 'Mobile' ? 'bg-blue-100 text-blue-700' :
                          log.device === 'Tablet' ? 'bg-purple-100 text-purple-700' :
                          'bg-zinc-100 text-zinc-700'
                        }`}>
                          {log.device || 'Unknown'}
                        </span>
                      </td>
                      <td className="py-2 text-zinc-600">{log.browser || 'Unknown'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-zinc-400 text-sm">No login history yet. Logins will be recorded here.</p>
          )}
        </div>
      )}
    </div>
  );
}
